import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useBoundStore } from '../store';
import { useI18n } from '../i18n';
import { buildTours, AUTO_START_ORDER } from './tours';
import { runTour } from './useDriverTour';

/**
 * Always-mounted controller (alongside UserRefresher) that auto-starts the
 * right first-time tour for the current route. Seeds "seen" state from the
 * server's user.onboarding, then for an unseen tour matching the route it
 * waits briefly (so the route's data-tour anchors mount) and launches it.
 * Each tour runs at most once — completion/skip persists to the backend.
 */
export const OnboardingController: React.FC = () => {
  const { t } = useI18n();
  const location = useLocation();
  const user = useBoundStore((s) => s.user);
  const onboarding = user?.onboarding;
  const activeTourId = useBoundStore((s) => s.activeTourId);
  const seenTours = useBoundStore((s) => s.seenTours);
  const startTour = useBoundStore((s) => s.startTour);
  const markTourSeen = useBoundStore((s) => s.markTourSeen);
  const hydrateOnboarding = useBoundStore((s) => s.hydrateOnboarding);

  // Seed seen tours / dismissed tips from the server payload.
  useEffect(() => {
    hydrateOnboarding(onboarding);
  }, [onboarding, hydrateOnboarding]);

  useEffect(() => {
    if (!user || activeTourId) return;

    const path = location.pathname;
    const tours = buildTours(t);
    const tour = AUTO_START_ORDER.map((id) => tours[id]).find(
      (tr) => tr.matchRoute(path) && !seenTours.includes(tr.id),
    );
    if (!tour) return;

    // Let the route's anchors mount before measuring/highlighting. The cleanup
    // cancels this if the user navigates away first.
    const timer = setTimeout(() => {
      const state = useBoundStore.getState();
      if (state.activeTourId || state.seenTours.includes(tour.id)) return;
      startTour(tour.id);
      runTour(tour, t, () =>
        markTourSeen(tour.id, tour.id === 'welcome'),
      );
    }, 800);

    return () => clearTimeout(timer);
  }, [user, activeTourId, seenTours, location.pathname, t, startTour, markTourSeen]);

  return null;
};

export default OnboardingController;
